import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import * as authService from "@/services/authService";
import type { ProfileDto, SessionProfile } from "@/services/authService";
import { onSessionExpired } from "@/services/http";
import { startOutboxSync } from "@/offline/outbox";
import { prefetchForOffline } from "@/offline/prefetch";
import { getOfflineStatus, subscribeOfflineStatus } from "@/offline/status";
import { listSchools } from "@/services/platformService";
import { getCycles, getSchoolInfo, primeActiveSchoolYear } from "@/services/schoolService";
import { cycleIdsForScope } from "@/utils/cycleScope";
import type { AppUser, Cycle, CycleScope, Role, School, SchoolInfo } from "@/types";

interface AuthContextValue {
  /** Utilisateur actuellement connecté (null = personne). */
  user: AppUser | null;
  role: Role | null;
  /** Ids de permissions effectifs de l'utilisateur connecté. */
  permissions: string[];
  cycleScope: CycleScope;
  /** Cycles de l'établissement (repérés par leur code PRIMAIRE / SECONDAIRE). */
  cycles: Cycle[];
  /** Cycles visibles pour l'utilisateur (1 seul si rôle scopé, les 2 si global). */
  allowedCycleIds: string[];
  /** Vrai tant que la session se charge. */
  loading: boolean;
  /** Écoles de la plateforme (super-administrateur uniquement). */
  schools: School[];
  /** École de l’utilisateur connecté (null pour le super-administrateur : console seule). */
  activeSchool: School | null;
  activeSchoolId: string | null;
  /** Vrai si l'utilisateur connecté est le super-admin de la plateforme. */
  isPlatformAdmin: boolean;
  /** Vrai si l'utilisateur possède la permission demandée (ex. "students:read"). */
  can: (permission: string) => boolean;
  /** Vrai si le cycle donné est visible pour l'utilisateur connecté. */
  isCycleVisible: (cycleId: string) => boolean;
  /** Connexion (email + mot de passe) ; lève une erreur avec un message lisible en cas d'échec. */
  login: (email: string, password: string, remember: boolean) => Promise<AppUser>;
  /** Déconnexion (invalide la session côté serveur). */
  logout: () => Promise<void>;
  /** Changement de son mot de passe ; la session est close (reconnexion avec le nouveau). */
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  /** Modification de son propre profil (nom, prénom, téléphone, photo). */
  updateProfile: (dto: Partial<ProfileDto>) => Promise<void>;
  /** Recharge le profil, l’école et les écoles (après une modification). */
  refresh: () => Promise<void>;
  /** Recharge la liste des écoles (après une modification). */
  refreshSchools: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Hook du contexte, volontairement à côté de son fournisseur (seul le rechargement à chaud en pâtit).
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth doit être utilisé dans un AuthProvider.");
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [schools, setSchools] = useState<School[]>([]);
  const [cycles, setCycles] = useState<Cycle[]>([]);
  /** Profil renvoyé par le backend (`/auth/me`). */
  const [session, setSession] = useState<SessionProfile | null>(null);
  const [schoolInfo, setSchoolInfo] = useState<SchoolInfo | null>(null);
  const [loading, setLoading] = useState(true);

  /** Oublie la session et les données de l’école (déconnexion, session expirée). */
  const forget = useCallback(() => {
    setSession(null);
    setSchools([]);
    setSchoolInfo(null);
    setCycles([]);
  }, []);

  /* Reprise de la session au chargement (jeton de renouvellement conservé). */
  useEffect(() => {
    let alive = true;
    authService
      .restoreSession()
      .then((profile) => {
        if (alive && profile) setSession(profile);
      })
      .catch(() => undefined)
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  /*
   * Session ouverte : le super-administrateur charge les écoles de la plateforme ; un utilisateur
   * d’école charge son école et son référentiel (cycles, année active). Un mot de passe provisoire
   * bloque tout autre appel tant qu’il n’est pas changé.
   */
  useEffect(() => {
    if (!session || session.user.mustChangePassword) return;
    let alive = true;
    if (session.user.isPlatformAdmin) {
      listSchools()
        .then((list) => {
          if (alive) setSchools(list);
        })
        .catch(() => undefined);
    } else {
      Promise.all([getCycles(), getSchoolInfo(), primeActiveSchoolYear()])
        .then(([cycleList, info]) => {
          if (!alive) return;
          setCycles(cycleList);
          setSchoolInfo(info);
        })
        .catch(() => undefined);
    }
    return () => {
      alive = false;
    };
  }, [session]);

  /* Session expirée côté serveur : on oublie l'utilisateur (le tableau de bord renvoie à la connexion). */
  useEffect(() => {
    onSessionExpired(forget);
    return () => onSessionExpired(null);
  }, [forget]);

  const user = session?.user ?? null;
  const role = session?.role ?? null;
  const isPlatformAdmin = user?.isPlatformAdmin === true;

  const permissions = useMemo(() => role?.permissions ?? [], [role]);
  const cycleScope: CycleScope = role?.cycleScope ?? "GLOBAL";

  const allowedCycleIds = useMemo(() => cycleIdsForScope(cycleScope, cycles), [cycleScope, cycles]);

  const activeSchoolId = isPlatformAdmin ? null : user?.schoolId ?? null;
  const activeSchool = useMemo<School | null>(() => {
    if (!user || !activeSchoolId || !schoolInfo) return null;
    return {
      id: activeSchoolId,
      name: schoolInfo.name,
      shortName: schoolInfo.name,
      code: "",
      city: schoolInfo.city,
      country: schoolInfo.country,
      address: schoolInfo.address,
      phone: schoolInfo.phone,
      email: schoolInfo.email,
      slogan: schoolInfo.slogan,
      logo: schoolInfo.logo,
      schoolYear: schoolInfo.schoolYear,
      cycles: cycles.map((c) => c.code),
      studentsCount: 0,
      staffCount: 0,
      usersCount: 0,
      isActive: true,
      plan: schoolInfo.plan,
      subscriptionEndsAt: schoolInfo.subscriptionEndsAt,
      createdAt: user.createdAt,
    };
  }, [user, activeSchoolId, schoolInfo, cycles]);

  const can = useCallback(
    (permission: string) => permissions.includes(permission) || permissions.includes("*"),
    [permissions],
  );

  /*
   * Hors ligne (utilisateur d'école) : envoi automatique des actions gardées sur l'appareil, et préparation
   * des données des pages principales à l'ouverture puis à chaque retour de la connexion.
   */
  const offlineReady = !!user && !isPlatformAdmin && !user.mustChangePassword;
  useEffect(() => {
    if (!offlineReady) return;
    startOutboxSync();
    void prefetchForOffline(can);
    return subscribeOfflineStatus(() => {
      if (getOfflineStatus().online) void prefetchForOffline(can);
    });
  }, [offlineReady, can]);

  const isCycleVisible = useCallback(
    (cycleId: string) => allowedCycleIds.includes(cycleId),
    [allowedCycleIds],
  );

  const login = useCallback(async (email: string, password: string, remember: boolean): Promise<AppUser> => {
    const profile = await authService.login(email.trim().toLowerCase(), password, remember);
    setSession(profile);
    return profile.user;
  }, []);

  const logout = useCallback(async () => {
    await authService.logout();
    forget();
  }, [forget]);

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      await authService.changePassword(currentPassword, newPassword);
      forget();
    },
    [forget],
  );

  const updateProfile = useCallback(async (dto: Partial<ProfileDto>) => {
    setSession(await authService.updateProfile(dto));
  }, []);

  const refreshSchools = useCallback(async () => {
    if (isPlatformAdmin) setSchools(await listSchools());
    else if (activeSchoolId) setSchoolInfo(await getSchoolInfo());
  }, [isPlatformAdmin, activeSchoolId]);

  const refresh = useCallback(async () => {
    if (!session) return;
    setSession(await authService.fetchProfile());
    await refreshSchools();
  }, [refreshSchools, session]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      role,
      permissions,
      cycleScope,
      cycles,
      allowedCycleIds,
      loading,
      schools,
      activeSchool,
      activeSchoolId,
      isPlatformAdmin,
      can,
      isCycleVisible,
      login,
      logout,
      changePassword,
      updateProfile,
      refresh,
      refreshSchools,
    }),
    [
      user,
      role,
      permissions,
      cycleScope,
      cycles,
      allowedCycleIds,
      loading,
      schools,
      activeSchool,
      activeSchoolId,
      isPlatformAdmin,
      can,
      isCycleVisible,
      login,
      logout,
      changePassword,
      updateProfile,
      refresh,
      refreshSchools,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
